/**
 * Query keys, fetchers, and mutations.
 *
 * One convention throughout: a mutation invalidates the keys its write could
 * have changed, and access views are invalidated by *every* grant write. A
 * grant added three levels up changes the answer for every node beneath it, and
 * a stale effective-access screen is worse than no screen — §4.8's whole
 * argument is that these views are the thing people trust when they cannot
 * reason about precedence themselves.
 */

import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from './client.ts';
import type {
  AcknowledgeResponse,
  AcknowledgmentCycleResponse,
  AcknowledgmentHistoryResponse,
  AddRelationshipResponse,
  ApprovalMutationResponse,
  ApprovalRequestResponse,
  ApprovalsAwaitingResponse,
  ArchiveDocumentResponse,
  AuthorMutationResponse,
  AuthorsResponse,
  ComplianceResponse,
  CreateDocumentResponse,
  CreateGrantResponse,
  CreateOrganizationResponse,
  CreatePersonResponse,
  CreateProjectResponse,
  CreateSpaceResponse,
  CreateUserResponse,
  CreateVersionResponse,
  DeactivationPreview,
  DeactivationResult,
  DiffResponse,
  DiffRevisionRef,
  DocumentAcknowledgmentsResponse,
  DocumentApprovalsResponse,
  DocumentResponse,
  DocumentType,
  GrantEffect,
  GrantListResponse,
  GrantSubject,
  MeResponse,
  NodeAccessResponse,
  NodeDetailResponse,
  NodeType,
  NotificationListResponse,
  OpenCycleResponse,
  OrganizationDetailResponse,
  OrganizationListResponse,
  OrganizationRelationship,
  OrgRelationshipType,
  OwedAcknowledgmentsResponse,
  PeopleListResponse,
  PersonAccessResponse,
  PersonDetailResponse,
  PickerResponse,
  ProjectActivityResponse,
  ProjectDetailResponse,
  ProjectListResponse,
  PublishConfirmationResponse,
  PublishResponse,
  RestoreVersionResponse,
  ReviewDueResponse,
  ReviewerDecision,
  ReviewerRequirement,
  SearchKind,
  SearchResults,
  SpaceDetailResponse,
  SpaceKind,
  SpaceListResponse,
  SpaceNodesResponse,
  SubsidiaryGrantResponse,
  SubsidiaryListResponse,
  TreeNode,
  UnpublishConfirmationResponse,
  UnpublishResponse,
  UpdateDocumentResponse,
  UpdateUserResponse,
  UserListResponse,
  UserRole,
  UserStatus,
  VersionDetailResponse,
  VersionListResponse,
} from './types.ts';

export const keys = {
  me: ['me'] as const,
  people: (filters?: unknown) => ['people', filters ?? {}] as const,
  person: (id: string) => ['person', id] as const,
  picker: ['people', 'picker'] as const,
  users: (filters?: unknown) => ['users', filters ?? {}] as const,
  deactivation: (id: string) => ['person', id, 'deactivation'] as const,
  organizations: (relationship?: string) => ['organizations', relationship ?? 'all'] as const,
  organization: (id: string) => ['organization', id] as const,
  subsidiaries: (id: string) => ['organization', id, 'subsidiaries'] as const,
  spaces: (capability?: string, scope?: string) =>
    ['spaces', capability ?? 'document.view', scope ?? 'all'] as const,
  space: (id: string) => ['space', id] as const,
  spaceNodes: (id: string, capability?: string) =>
    ['space', id, 'nodes', capability ?? 'document.view'] as const,
  node: (id: string) => ['node', id] as const,
  grants: (subjectType: GrantSubject, subjectId: string) =>
    ['grants', subjectType, subjectId] as const,
  accessNode: (nodeId: string, capability: string) =>
    ['access', 'node', nodeId, capability] as const,
  accessPerson: (personId: string, capability?: string, spaceId?: string) =>
    ['access', 'person', personId, capability ?? 'all', spaceId ?? 'all'] as const,
  notifications: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,

  /**
   * Everything about one document hangs off `['document', nodeId, …]`, so a
   * write that could have changed any of it invalidates the prefix rather than
   * naming five keys and forgetting the sixth. §7 gives that prefix real work:
   * a save snapshots a version, a publish promotes one and moves the node, and
   * a restore rewrites the draft — three writes that each touch the body, the
   * version list, and the diff at once.
   */
  document: (nodeId: string, version?: string) =>
    ['document', nodeId, 'body', version ?? 'current'] as const,
  documentMarkdown: (nodeId: string, version?: string) =>
    ['document', nodeId, 'markdown', version ?? 'current'] as const,
  documentVersions: (nodeId: string, page?: unknown) =>
    ['document', nodeId, 'versions', page ?? {}] as const,
  documentVersion: (nodeId: string, versionId: string) =>
    ['document', nodeId, 'version', versionId] as const,
  documentDiff: (nodeId: string, from?: string, to?: string) =>
    ['document', nodeId, 'diff', from ?? 'published', to ?? 'auto'] as const,
  documentAuthors: (nodeId: string) => ['document', nodeId, 'authors'] as const,
  publishPreview: (nodeId: string, spaceId?: string, parentNodeId?: string) =>
    ['document', nodeId, 'publish', spaceId ?? 'default', parentNodeId ?? 'root'] as const,
  unpublishPreview: (nodeId: string) => ['document', nodeId, 'unpublish'] as const,

  /**
   * The two 1b workflows hang off the same document prefix wherever they are
   * *about* one document, and that placement is doing work rather than being
   * tidy. §7.6's gate blocks with `draft_changed` the moment somebody saves
   * after an approval, so a save that already invalidates `['document', nodeId]`
   * refreshes the gate too — without `useUpdateDocument` having to know that an
   * approval domain exists.
   *
   * The person-scoped and id-scoped keys sit under their own prefixes, because
   * "what is waiting on me" and "one request" are not about a document that the
   * cache can name.
   */
  documentApprovals: (nodeId: string) => ['document', nodeId, 'approvals'] as const,
  documentAcknowledgments: (nodeId: string, currentOnly?: boolean) =>
    ['document', nodeId, 'acknowledgments', currentOnly ?? false] as const,
  approvalsAwaiting: ['approvals', 'mine'] as const,
  approvalRequest: (requestId: string) => ['approvals', 'request', requestId] as const,
  acknowledgmentsOwed: ['acknowledgments', 'mine'] as const,
  acknowledgmentHistory: ['acknowledgments', 'history'] as const,
  acknowledgmentCycle: (requestId: string) => ['acknowledgments', 'cycle', requestId] as const,
  compliance: (filters?: unknown) => ['acknowledgments', 'compliance', filters ?? {}] as const,

  search: (filters: unknown) => ['search', filters ?? {}] as const,
  reviewDue: (scope: 'mine' | 'all') => ['review-due', scope] as const,
};

// --- session --------------------------------------------------------------

/**
 * Shared by the router's `beforeLoad` guard and by every screen that renders
 * the signed-in person, so the shell resolves identity once per load rather
 * than once per component.
 */
export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: ({ signal }) => api.get<MeResponse>('/auth/me', undefined, signal),
  // §2.3's rolling expiry is refreshed by the API on activity; re-asking on
  // every focus is how the shell notices a session that was revoked elsewhere
  // (a deactivation, or "sign out everywhere").
  staleTime: 30_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQuery);
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ status: string }>('/auth/sign-out'),
    onSuccess: () => queryClient.clear(),
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ status: string; message: string }>('/auth/magic-link', { email }),
  });
}

// --- people (§2.1, §2.4) --------------------------------------------------

export interface PeopleFilters {
  status?: UserStatus;
  role?: UserRole;
  q?: string;
  /** Whether they can sign in at all — `none` is the CRM list (phase 2 §2.1). */
  account?: 'any' | 'none';
}

export function usePeople(filters: PeopleFilters) {
  return useQuery({
    queryKey: keys.people(filters),
    queryFn: ({ signal }) =>
      api.get<PeopleListResponse>(
        '/api/people',
        {
          status: filters.status,
          role: filters.role,
          q: filters.q,
          account: filters.account,
          limit: 200,
        },
        signal,
      ),
  });
}

/**
 * The picker (`?picker=true`) returns only active people and only the fields a
 * picker renders — which is also what §2.6 requires: "Deactivated people never
 * appear in assignee or reviewer pickers."
 */
export function usePeoplePicker(enabled = true) {
  return useQuery({
    queryKey: keys.picker,
    queryFn: ({ signal }) =>
      api.get<PickerResponse>('/api/people', { picker: 'true', limit: 200 }, signal),
    enabled,
  });
}

export function usePerson(id: string) {
  return useQuery({
    queryKey: keys.person(id),
    queryFn: ({ signal }) => api.get<PersonDetailResponse>(`/api/people/${id}`, undefined, signal),
  });
}

/** §2.5 — a person, and nothing addressed to them. Access is the call below. */
export function useCreatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; name: string }) =>
      api.post<CreatePersonResponse>('/api/people', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/** The record: a name and an email. A role is not here — it is the account's. */
export function useUpdatePerson(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; email?: string }) =>
      api.patch<{ person: PersonDetailResponse['person'] }>(`/api/people/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people'] });
      void queryClient.invalidateQueries({ queryKey: keys.person(id) });
    },
  });
}

// --- users (phase 2 §2.1) -------------------------------------------------

export interface UserFilters {
  status?: UserStatus;
  role?: UserRole;
  q?: string;
}

/** Everyone who can sign in. The people with no account are not rows it hides. */
export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: keys.users(filters),
    queryFn: ({ signal }) =>
      api.get<UserListResponse>(
        '/api/users',
        { status: filters.status, role: filters.role, q: filters.q, limit: 200 },
        signal,
      ),
  });
}

/**
 * "Give this person access" (§2.5) — the act that creates the login and sends
 * the invitation. Invalidates the access queries as well, because a new account
 * is a new baseline and every grant already naming them starts resolving.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; role: UserRole; authMethod?: 'sso' | 'magic_link' }) =>
      api.post<CreateUserResponse>('/api/users', input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: ['people'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: keys.person(input.personId) });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
  });
}

export function useUpdateUser(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { role: UserRole }) =>
      api.patch<UpdateUserResponse>(`/api/users/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: keys.person(id) });
      // A role change moves the person's entire §4.4 baseline without touching
      // a grant row, so every access answer about them is now suspect.
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
  });
}

export function useResendInvitation(id: string) {
  return useMutation({
    mutationFn: () =>
      api.post<{ invitation: { sent: boolean; error: string | null } }>(
        `/api/users/${id}/invitations`,
      ),
  });
}

// --- deactivation (§2.6) --------------------------------------------------

export function useDeactivationPreview(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.deactivation(id),
    queryFn: ({ signal }) =>
      api.get<DeactivationPreview>(`/api/users/${id}/deactivation`, undefined, signal),
    enabled,
    // The blocker list is the input to a decision someone is making right now.
    staleTime: 0,
  });
}

export interface DeactivationInput {
  reassignments?: Array<{ nodeId: string; ownerId: string }>;
  reassignAllTo?: string;
  reason?: string;
}

export function useDeactivate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeactivationInput) =>
      api.post<DeactivationResult>(`/api/users/${id}/deactivation`, input),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

// --- organizations (§3) ---------------------------------------------------

/**
 * Phase 2 §7.2 — "Clients and Organizations are two views of one table."
 *
 * The filter is the API's `relationship` parameter rather than a predicate over
 * a full list, so the two screens differ in what they ask for and not in what
 * they do with the answer. A client-side filter would make the Clients page a
 * second opinion about what a client is, and §3.2 already made that a row.
 */
export function useOrganizations(relationship?: OrgRelationshipType) {
  return useQuery({
    queryKey: keys.organizations(relationship),
    queryFn: ({ signal }) =>
      api.get<OrganizationListResponse>('/api/organizations', { limit: 200, relationship }, signal),
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: keys.organization(id),
    queryFn: ({ signal }) =>
      api.get<OrganizationDetailResponse>(`/api/organizations/${id}`, undefined, signal),
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      relationship: OrgRelationshipType;
      parentOrgId?: string;
      spaceName?: string;
    }) => api.post<CreateOrganizationResponse>('/api/organizations', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      // A client's paired spaces are new, so the space tree changed too (§3.1).
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

/**
 * Adding a relationship — and, when it is `client`, the conversion §3.3 makes
 * atomic. Invalidates the space tree and the access queries for that reason: a
 * conversion mints two spaces and writes the creator's grants on one of them.
 */
export function useAddRelationship(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: OrgRelationshipType; startedOn?: string; spaceName?: string }) =>
      api.post<AddRelationshipResponse>(`/api/organizations/${id}/relationships`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: keys.organization(id) });
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
  });
}

/** Ending an engagement. The space stays — §3.3, and it is not negotiable. */
export function useEndRelationship(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { relationshipId: string; endedOn: string }) =>
      api.patch<{ relationship: OrganizationRelationship }>(
        `/api/organizations/${id}/relationships/${input.relationshipId}`,
        { endedOn: input.endedOn },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: keys.organization(id) });
    },
  });
}

// --- projects (phase 2 §4) -------------------------------------------------

export function useProjects(filters: { organizationId?: string; status?: string } = {}) {
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: ({ signal }) =>
      api.get<ProjectListResponse>(
        '/api/projects',
        { organizationId: filters.organizationId, status: filters.status, limit: 200 },
        signal,
      ),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: ({ signal }) => api.get<ProjectDetailResponse>(`/api/projects/${id}`, {}, signal),
  });
}

/** §4.3 — "the tree must not be the only way to reach anything." */
export function useProjectActivity(id: string) {
  return useQuery({
    queryKey: ['projects', id, 'activity'],
    queryFn: ({ signal }) =>
      api.get<ProjectActivityResponse>(`/api/projects/${id}/activity`, {}, signal),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      organizationId: string;
      leadId?: string;
      clientContactIds?: string[];
    }) => api.post<CreateProjectResponse>('/api/projects', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      // The paired spaces are new, so the space tree changed too (§4.2).
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

export function useSubsidiaries(id: string) {
  return useQuery({
    queryKey: keys.subsidiaries(id),
    queryFn: ({ signal }) =>
      api.get<SubsidiaryListResponse>(`/api/organizations/${id}/subsidiaries`, undefined, signal),
  });
}

export function useAddMember(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; title?: string | null }) =>
      api.post(`/api/organizations/${organizationId}/members`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.organization(organizationId) });
      // Deliberately does *not* invalidate access: membership is not access
      // (§3.2), and nothing about a person's reach changed here.
    },
  });
}

export function useRemoveMember(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) =>
      api.delete<void>(`/api/organizations/${organizationId}/members/${personId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.organization(organizationId) });
    },
  });
}

export interface SubsidiaryGrantInput {
  principalType: 'person' | 'role';
  principalId: string;
  capability: string;
  effect: GrantEffect;
  includeParent: boolean;
  reason?: string;
}

/** §3.3 — "grant to all subsidiaries" writes N explicit rows, never a cascade. */
export function useGrantToSubsidiaries(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubsidiaryGrantInput) =>
      api.post<SubsidiaryGrantResponse>(
        `/api/organizations/${organizationId}/subsidiary-grants`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['grants'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

// --- spaces and nodes (§4.1, §4.2) ----------------------------------------

/**
 * Phase 2 §7.2's Knowledge/Projects split, as a `scope` the API resolves.
 *
 * "Entirely a view-layer change — the `space` table is untouched", and the view
 * layer that changes is this parameter: whether a space is knowledge is an
 * ancestry question, and a screen deriving it from a flat list would be
 * rebuilding the tree to answer it.
 */
export function useSpaces(capability?: string, scope?: 'all' | 'knowledge') {
  return useQuery({
    queryKey: keys.spaces(capability, scope),
    queryFn: ({ signal }) =>
      api.get<SpaceListResponse>('/api/spaces', { capability, scope }, signal),
  });
}

/**
 * Both space reads accept an empty id and stay idle for it.
 *
 * Callers reach these with an id that arrives one render late — a node page
 * knows its space only once the node has loaded, and the access views name a
 * grant's subject from a list they are still fetching. Without the guard those
 * screens open by requesting `/api/spaces//nodes`, which 404s on every visit
 * and puts a red herring in the log a person is reading to find a real denial
 * (§8.5).
 */
export function useSpace(id: string) {
  return useQuery({
    queryKey: keys.space(id),
    queryFn: ({ signal }) => api.get<SpaceDetailResponse>(`/api/spaces/${id}`, undefined, signal),
    enabled: id !== '',
  });
}

export function useSpaceNodes(id: string, capability?: string) {
  return useQuery({
    queryKey: keys.spaceNodes(id, capability),
    queryFn: ({ signal }) =>
      api.get<SpaceNodesResponse>(`/api/spaces/${id}/nodes`, { capability }, signal),
    enabled: id !== '',
  });
}

export function useCreateSpace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      kind: SpaceKind;
      parentSpaceId?: string | null;
      isTemplate?: boolean;
    }) => api.post<CreateSpaceResponse>('/api/spaces', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

export function useCreateNode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      spaceId: string;
      parentNodeId?: string | null;
      type: NodeType;
      title: string;
      ownerId?: string;
    }) => api.post<{ node: TreeNode }>('/api/nodes', input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: ['space', input.spaceId, 'nodes'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
  });
}

/**
 * Exported as `queryOptions` so a list can fan out over `useQueries` with the
 * same key and fetcher a detail screen uses — one cache entry per node, shared,
 * rather than a second copy under a list-shaped key.
 */
export function nodeQuery(id: string) {
  return queryOptions({
    queryKey: keys.node(id),
    queryFn: ({ signal }) => api.get<NodeDetailResponse>(`/api/nodes/${id}`, undefined, signal),
    enabled: id !== '',
  });
}

export function useNode(id: string) {
  return useQuery(nodeQuery(id));
}

export function useDeleteNode(spaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (nodeId: string) => api.delete<{ deleted: number }>(`/api/nodes/${nodeId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'nodes'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
    },
  });
}

export function useReassignNode(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ownerId: string; reason?: string }) =>
      api.post<{ node: TreeNode }>(`/api/nodes/${nodeId}/reassign`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
      void queryClient.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

// --- grants (§4.5) --------------------------------------------------------

export function useGrants(subjectType: GrantSubject, subjectId: string) {
  return useQuery({
    queryKey: keys.grants(subjectType, subjectId),
    queryFn: ({ signal }) =>
      api.get<GrantListResponse>('/api/grants', { subjectType, subjectId }, signal),
  });
}

export interface CreateGrantInput {
  subjectType: GrantSubject;
  subjectId: string;
  principalType: 'person' | 'role';
  principalId: string;
  capability: string;
  effect: GrantEffect;
  reason?: string;
}

export function useCreateGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGrantInput) => api.post<CreateGrantResponse>('/api/grants', input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: keys.grants(input.subjectType, input.subjectId),
      });
      // Every access answer beneath this subject may have moved.
      void queryClient.invalidateQueries({ queryKey: ['access'] });
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
      void queryClient.invalidateQueries({ queryKey: keys.unreadCount });
    },
  });
}

export function useRevokeGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; subjectType: GrantSubject; subjectId: string }) =>
      api.delete<void>(`/api/grants/${input.id}`),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: keys.grants(input.subjectType, input.subjectId),
      });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

// --- effective access (§4.8) ----------------------------------------------

export function useNodeAccess(nodeId: string, capability: string) {
  return useQuery({
    queryKey: keys.accessNode(nodeId, capability),
    queryFn: ({ signal }) =>
      api.get<NodeAccessResponse>(`/api/access/nodes/${nodeId}`, { capability }, signal),
  });
}

export function usePersonAccess(personId: string, capability?: string, spaceId?: string) {
  return useQuery({
    queryKey: keys.accessPerson(personId, capability, spaceId),
    queryFn: ({ signal }) =>
      api.get<PersonAccessResponse>(
        `/api/access/people/${personId}`,
        { capability, spaceId },
        signal,
      ),
  });
}

// --- notifications (§7.4) -------------------------------------------------

export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications,
    queryFn: ({ signal }) =>
      api.get<NotificationListResponse>('/api/notifications', { limit: 50 }, signal),
  });
}

/** §7.4 — "Poll on an interval — no WebSocket infrastructure for this." */
export function useUnreadCount() {
  return useQuery({
    queryKey: keys.unreadCount,
    queryFn: ({ signal }) =>
      api.get<{ unreadCount: number }>('/api/notifications/unread-count', undefined, signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      api.post<{ updated: number; unreadCount: number }>(
        '/api/notifications/read',
        ids ? { ids } : {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// --- documents (phase 1 §7) -----------------------------------------------

/**
 * The two content forms `POST`/`PATCH /api/documents` accept, never both.
 *
 * The API rejects the pair outright rather than preferring one — "they cannot
 * be reconciled" — and this union turns that 400 into a compile error. The
 * third arm is the metadata-only edit: a title or review-date change carries no
 * body at all.
 */
export type ContentInput =
  | { content: Record<string, unknown>; markdown?: never }
  | { markdown: string; content?: never }
  | { content?: never; markdown?: never };

export type CreateDocumentInput = {
  spaceId: string;
  parentNodeId?: string | null;
  title: string;
  type: DocumentType;
  /** Defaults to the creator. Naming anyone else requires `admin.override`. */
  ownerId?: string;
  /** §7.2 — `YYYY-MM-DD`, and only on a playbook or a reference. */
  reviewBy?: string | null;
  position?: number;
} & ContentInput;

export type UpdateDocumentInput = {
  title?: string;
  /** Null clears the date. §7.1 has no `type`: a document's type does not change. */
  reviewBy?: string | null;
} & ContentInput;

/**
 * One document and the body this caller resolves to (§7.4).
 *
 * Which body arrives is the server's decision, not a parameter: an editor gets
 * the working draft, a reader gets the published version, and `body.source`
 * says which. A screen that assumes it is looking at the draft will silently
 * show a reader stale-looking content and offer to edit it.
 *
 * Exported as `queryOptions` so a route loader can `ensureQueryData` it before
 * the editor mounts — a Yjs provider that connects while the REST body is still
 * in flight has nothing to seed the document with.
 */
export function documentQuery(nodeId: string, version?: string) {
  return queryOptions({
    queryKey: keys.document(nodeId, version),
    queryFn: ({ signal }) =>
      api.get<DocumentResponse>(`/api/documents/${nodeId}`, { version }, signal),
    enabled: nodeId !== '',
  });
}

export function useDocument(nodeId: string, version?: string) {
  return useQuery(documentQuery(nodeId, version));
}

/**
 * §5.1's wire format as text — copy-as-markdown and the raw endpoint (§12).
 *
 * `source` comes back from the `x-document-source` header, so a "copied" toast
 * can say *which* body was copied. Usable with `queryClient.fetchQuery` from a
 * button press, which is how a copy action should reach it: nothing needs this
 * resident in the cache.
 */
export function documentMarkdownQuery(nodeId: string, version?: string) {
  return queryOptions({
    queryKey: keys.documentMarkdown(nodeId, version),
    queryFn: ({ signal }) => api.text(`/api/documents/${nodeId}/markdown`, { version }, signal),
    enabled: nodeId !== '',
  });
}

export function useDocumentMarkdown(
  nodeId: string,
  options: { version?: string; enabled?: boolean } = {},
) {
  return useQuery({
    ...documentMarkdownQuery(nodeId, options.version),
    enabled: nodeId !== '' && (options.enabled ?? true),
  });
}

/**
 * The documents in one space.
 *
 * **There is no `GET /api/documents`.** §7 has no list endpoint — the tree is
 * the list, and `GET /api/spaces/:id/nodes` is already filtered through
 * `permission_visible_nodes`. So this shares that query's cache entry exactly
 * (same key, same request) and narrows the result with `select`; a parallel
 * key would fetch the same rows twice and let the two go stale apart.
 */
export function useSpaceDocuments(spaceId: string, capability = 'document.view') {
  return useQuery({
    queryKey: keys.spaceNodes(spaceId, capability),
    queryFn: ({ signal }) =>
      api.get<SpaceNodesResponse>(`/api/spaces/${spaceId}/nodes`, { capability }, signal),
    enabled: spaceId !== '',
    select: (data: SpaceNodesResponse) => ({
      space: data.space,
      documents: data.nodes.filter((node) => node.type === 'document'),
    }),
  });
}

export function useCreateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentInput) =>
      api.post<CreateDocumentResponse>('/api/documents', input),
    onSuccess: (_data, input) => {
      // A document is a node, so the same things move that `useCreateNode`
      // moves — plus search, which indexes the draft immediately (§9.3 shows
      // drafts to the people who can edit them).
      void queryClient.invalidateQueries({ queryKey: ['space', input.spaceId, 'nodes'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

/**
 * A save (§7.4, §7.5).
 *
 * Writes the working draft and snapshots it, coalescing into the author's open
 * burst. `published_version_id` is untouched, so readers keep resolving to what
 * they were reading — which is why this does *not* invalidate anything about
 * other people's view of the corpus beyond search.
 */
export function useUpdateDocument(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDocumentInput) =>
      api.patch<UpdateDocumentResponse>(`/api/documents/${nodeId}`, input),
    onSuccess: (data) => {
      // The prefix, not `keys.document`: a save may have created a version and
      // certainly moved the diff.
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      // A title change is a `node` change, mirrored into the tree by
      // `node_propagate_title_trg`.
      void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
      void queryClient.invalidateQueries({
        queryKey: ['space', data.document.spaceId, 'nodes'],
      });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
      // A new review date can pull a document back out of `needs_review`
      // (`statusAfterReviewDateChange`), which is the digest list shrinking.
      void queryClient.invalidateQueries({ queryKey: ['review-due'] });
    },
  });
}

/**
 * The confirmation §7.4 requires, before anything is published.
 *
 * `staleTime: 0` for the same reason the deactivation preview has it: this is
 * the input to a decision someone is making right now, and it is computed by
 * running the publish and rolling it back — a cached audience is an audience
 * from before the last grant.
 */
export function publishPreviewQuery(
  nodeId: string,
  destination: { spaceId?: string; parentNodeId?: string } = {},
) {
  return queryOptions({
    queryKey: keys.publishPreview(nodeId, destination.spaceId, destination.parentNodeId),
    queryFn: ({ signal }) =>
      api.get<PublishConfirmationResponse>(
        `/api/documents/${nodeId}/publish`,
        { spaceId: destination.spaceId, parentNodeId: destination.parentNodeId },
        signal,
      ),
    staleTime: 0,
    gcTime: 0,
  });
}

export function usePublishPreview(
  nodeId: string,
  destination: { spaceId?: string; parentNodeId?: string } = {},
  enabled = true,
) {
  return useQuery({ ...publishPreviewQuery(nodeId, destination), enabled });
}

export interface PublishInput {
  /**
   * §7.4 — the API refuses a bare POST, and this flag asserts that the
   * confirmation was rendered and accepted. Taken from the caller rather than
   * hardcoded here, so a screen that publishes without showing one has to write
   * the claim out itself.
   */
  confirmed: true;
  spaceId?: string;
  parentNodeId?: string | null;
  /** §6.4 — an admin publishing past `document.publish`. Requires `reason`. */
  force?: boolean;
  reason?: string;
}

/**
 * Publish — and the widest invalidation in this file, on purpose.
 *
 * ADR 0004: publish **moves the node**, with its whole subtree, into the
 * sibling shared space. Three caches move together and all three are listed in
 * §7.4's promise: the document (status and `publishedVersionId`), the version
 * list (the draft was promoted to a new version), and the tree (the node is in
 * a different space).
 *
 * The tree half is invalidated as the whole `['space']` prefix rather than the
 * two spaces involved, because {@link PublishResponse} carries the destination
 * and not the origin — there is no `plan.from` in the POST body. Naming the
 * destination alone would leave the space the document was published *out of*
 * still showing it, which is the exact stale view that makes people believe
 * publishing did nothing.
 *
 * `['access']` goes too: ADR 0004's correctness obligation is that node-level
 * overrides are re-evaluated against the new ancestry, so every §4.8 answer
 * about anything in the moved subtree may have changed.
 */
export function usePublishDocument(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PublishInput) =>
      api.post<PublishResponse>(`/api/documents/${nodeId}/publish`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
      void queryClient.invalidateQueries({ queryKey: ['space'] });
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
      void queryClient.invalidateQueries({ queryKey: ['review-due'] });
    },
  });
}

/**
 * The unpublish confirmation (phase 2 §4.4).
 *
 * `staleTime: 0` and `gcTime: 0` for publish's reason and one more: the
 * engagement answer is what decides whether the primary button is live at all,
 * and a client comment posted two minutes ago is exactly the fact a cached copy
 * would be missing.
 */
export function unpublishPreviewQuery(nodeId: string) {
  return queryOptions({
    queryKey: keys.unpublishPreview(nodeId),
    queryFn: ({ signal }) =>
      api.get<UnpublishConfirmationResponse>(
        `/api/documents/${nodeId}/unpublish`,
        undefined,
        signal,
      ),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useUnpublishPreview(nodeId: string, enabled = true) {
  return useQuery({ ...unpublishPreviewQuery(nodeId), enabled });
}

export interface UnpublishInput {
  confirmed: true;
  /** §4.4's escape hatch — an admin withdrawing past the engagement refusal. */
  force?: boolean;
  reason?: string;
}

/**
 * Unpublish, invalidating exactly what publish does and for the same reason:
 * the node moved, so the trees on both sides of the move are stale, the §4.8
 * answers about everything in the subtree may have changed, and search now
 * resolves for a smaller set of people.
 *
 * The document itself is invalidated even though this route writes no document
 * row. `spaceId` on the view comes from the node, and the node is what moved.
 */
export function useUnpublishDocument(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UnpublishInput) =>
      api.post<UnpublishResponse>(`/api/documents/${nodeId}/unpublish`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
      void queryClient.invalidateQueries({ queryKey: ['space'] });
      void queryClient.invalidateQueries({ queryKey: ['spaces'] });
      void queryClient.invalidateQueries({ queryKey: ['access'] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

/**
 * Archive and unarchive (§7.4).
 *
 * Both change who *finds* the document rather than what it says — §9.3 excludes
 * archived from search "unless explicitly requested", and `documentsPastReview`
 * stops counting it — so search and the review list are the two things that
 * must not keep their old answer. The tree is untouched: a direct link still
 * resolves, which is the whole distinction between archiving and deleting.
 */
function useArchiveTransition(nodeId: string, path: 'archive' | 'unarchive') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ArchiveDocumentResponse>(`/api/documents/${nodeId}/${path}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
      void queryClient.invalidateQueries({ queryKey: ['review-due'] });
    },
  });
}

export function useArchiveDocument(nodeId: string) {
  return useArchiveTransition(nodeId, 'archive');
}

export function useUnarchiveDocument(nodeId: string) {
  return useArchiveTransition(nodeId, 'unarchive');
}

// --- versions (§7.5) -------------------------------------------------------

export interface VersionPage {
  limit?: number;
  offset?: number;
}

export function documentVersionsQuery(nodeId: string, page: VersionPage = {}) {
  return queryOptions({
    queryKey: keys.documentVersions(nodeId, page),
    queryFn: ({ signal }) =>
      api.get<VersionListResponse>(
        `/api/documents/${nodeId}/versions`,
        { limit: page.limit, offset: page.offset },
        signal,
      ),
    enabled: nodeId !== '',
  });
}

export function useDocumentVersions(nodeId: string, page: VersionPage = {}) {
  return useQuery(documentVersionsQuery(nodeId, page));
}

export function documentVersionQuery(nodeId: string, versionId: string) {
  return queryOptions({
    queryKey: keys.documentVersion(nodeId, versionId),
    queryFn: ({ signal }) =>
      api.get<VersionDetailResponse>(
        `/api/documents/${nodeId}/versions/${versionId}`,
        undefined,
        signal,
      ),
    enabled: nodeId !== '' && versionId !== '',
    // A version is immutable once written, apart from the coalescing window
    // that can rewrite the newest one. Nothing else in this file is allowed to
    // go this stale.
    staleTime: 5 * 60_000,
  });
}

export function useDocumentVersion(nodeId: string, versionId: string) {
  return useQuery(documentVersionQuery(nodeId, versionId));
}

/**
 * The session-end snapshot §7.5 names — forced, not coalesced, so the next edit
 * starts a new version. `created: false` means the body was identical to the
 * newest version and nothing was written.
 */
export function useCreateVersion(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CreateVersionResponse>(`/api/documents/${nodeId}/versions`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
    },
  });
}

/**
 * Restore (§7.5) — the version's content becomes the working draft, and the
 * restore is itself snapshotted. Readers are unaffected until someone
 * publishes, so this invalidates the document and search, not the tree.
 */
export function useRestoreVersion(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      api.post<RestoreVersionResponse>(`/api/documents/${nodeId}/versions/${versionId}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

/**
 * A diff between two revisions, rendered from markdown and never from JSON
 * (§5.1, ADR 0002).
 *
 * Both refs default on the server to the question people actually ask — what
 * has changed since the team last saw this — so passing neither is the common
 * case rather than a lazy one.
 */
export function documentDiffQuery(
  nodeId: string,
  refs: { from?: DiffRevisionRef; to?: DiffRevisionRef; context?: number } = {},
) {
  return queryOptions({
    queryKey: keys.documentDiff(nodeId, refs.from, refs.to),
    queryFn: ({ signal }) =>
      api.get<DiffResponse>(
        `/api/documents/${nodeId}/diff`,
        { from: refs.from, to: refs.to, context: refs.context },
        signal,
      ),
    enabled: nodeId !== '',
  });
}

export function useDocumentDiff(
  nodeId: string,
  refs: { from?: DiffRevisionRef; to?: DiffRevisionRef; context?: number } = {},
) {
  return useQuery(documentDiffQuery(nodeId, refs));
}

// --- authorship (§7.3) -----------------------------------------------------

export function documentAuthorsQuery(nodeId: string) {
  return queryOptions({
    queryKey: keys.documentAuthors(nodeId),
    queryFn: ({ signal }) =>
      api.get<AuthorsResponse>(`/api/documents/${nodeId}/authors`, undefined, signal),
    enabled: nodeId !== '',
  });
}

export function useDocumentAuthors(nodeId: string) {
  return useQuery(documentAuthorsQuery(nodeId));
}

/**
 * A credit for someone who contributed outside the editor.
 *
 * This grants nothing. §7.3 keeps editing rights in the ACL — "a per-document
 * editor roster would create two systems answering 'can Sarah edit this'" — so
 * a person credited here gains a line in the byline and no access at all. A
 * screen that presents this as "add an editor" is describing a feature that
 * does not exist.
 */
export function useAddAuthor(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; note?: string }) =>
      api.post<AuthorMutationResponse>(`/api/documents/${nodeId}/authors`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documentAuthors(nodeId) });
    },
  });
}

export function useRemoveAuthor(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) =>
      api.delete<AuthorMutationResponse>(`/api/documents/${nodeId}/authors/${personId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.documentAuthors(nodeId) });
    },
  });
}

// --- review dates (§7.2) ---------------------------------------------------

/**
 * "12 playbooks are past review" — §7.2 calls this a bigger behaviour change
 * than any feature in the spec.
 *
 * `mine` is the default and is the nag: §7.2 nags the owner, singular, because
 * a nag that reaches everyone who *could* fix it is a nag nobody owns. `all` is
 * §7.7's cross-document compliance view and is not what a person's own screen
 * should ask for.
 */
export function reviewDueQuery(scope: 'mine' | 'all' = 'mine') {
  return queryOptions({
    queryKey: keys.reviewDue(scope),
    queryFn: ({ signal }) =>
      api.get<ReviewDueResponse>('/api/documents/review/due', { scope }, signal),
  });
}

export function useReviewDue(scope: 'mine' | 'all' = 'mine') {
  return useQuery(reviewDueQuery(scope));
}

// --- search (§9) -----------------------------------------------------------

export interface SearchFilters {
  q: string;
  /**
   * Absent searches all three node types.
   *
   * **An empty array does not mean "none".** It serialises to an empty string,
   * which is dropped from the query string, which the route reads as "no
   * filter" — so unchecking every box would widen the search instead of
   * emptying it. The route's own schema requires at least one kind, so decide
   * what "nothing selected" means in the UI before it reaches here.
   */
  kinds?: readonly SearchKind[];
  /** One space exactly, not its subtree. */
  spaceId?: string;
  /** §9.3 — archived is excluded unless explicitly requested. */
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Search — §9.
 *
 * There is no 403 here and there never will be: §9.2 joins
 * `permission_visible_nodes` inside the one statement, so an unreachable
 * document is not refused, it is never a row. A screen that renders an
 * `ApiError.forbidden` branch for search is handling a case the API cannot
 * produce — and a 403 would itself confirm that something matched.
 */
export function searchQuery(filters: SearchFilters) {
  const term = filters.q.trim();
  return queryOptions({
    queryKey: keys.search({ ...filters, q: term }),
    queryFn: ({ signal }) =>
      api.get<SearchResults>(
        '/api/search',
        {
          q: term,
          kind: filters.kinds?.join(','),
          spaceId: filters.spaceId,
          includeArchived: filters.includeArchived,
          limit: filters.limit,
          offset: filters.offset,
        },
        signal,
      ),
    enabled: term.length > 0,
    // Paging and typing both re-key this. Holding the previous page keeps the
    // list from collapsing to a spinner between keystrokes, which is the
    // difference between search feeling instant and feeling broken.
    placeholderData: keepPreviousData,
  });
}

export function useSearch(filters: SearchFilters) {
  return useQuery(searchQuery(filters));
}

// --- approvals (§7.6) ------------------------------------------------------

/**
 * §7.2 — "which reviews are waiting on me". The reviewer's own inbox.
 *
 * Refetched on focus for the same reason the unread count polls: somebody who
 * left this tab open while a colleague opened a request should not have to
 * reload to find out they are blocking a publish.
 */
export function useApprovalsAwaitingMe() {
  return useQuery({
    queryKey: keys.approvalsAwaiting,
    queryFn: ({ signal }) =>
      api.get<ApprovalsAwaitingResponse>('/api/approvals/mine', undefined, signal),
    refetchOnWindowFocus: true,
  });
}

/**
 * One document's approval history *and* the gate, from the one call that serves
 * both.
 *
 * The gate is the answer publish will refuse on, and §7.4 requires it to be
 * renderable **before** the button is pressed rather than as the body of the
 * 409 that pressing it produces. `staleTime: 0` because it is the input to a
 * decision somebody is making right now — the same rule the publish preview
 * carries, and for the same reason: a cached gate is a gate from before the last
 * decision.
 */
export function documentApprovalsQuery(nodeId: string) {
  return queryOptions({
    queryKey: keys.documentApprovals(nodeId),
    queryFn: ({ signal }) =>
      api.get<DocumentApprovalsResponse>(`/api/approvals/documents/${nodeId}`, undefined, signal),
    enabled: nodeId !== '',
    staleTime: 0,
  });
}

export function useDocumentApprovals(nodeId: string) {
  return useQuery(documentApprovalsQuery(nodeId));
}

export function useApprovalRequest(requestId: string) {
  return useQuery({
    queryKey: keys.approvalRequest(requestId),
    queryFn: ({ signal }) =>
      api.get<ApprovalRequestResponse>(`/api/approvals/${requestId}`, undefined, signal),
    enabled: requestId !== '',
    staleTime: 0,
  });
}

/**
 * The invalidation every approval mutation shares, written once.
 *
 * `gate.nodeId` comes back in the response rather than being closed over, which
 * is what lets one helper serve the five routes — including opening a request,
 * where the caller knows the document, and deciding, where it knows only the
 * request id.
 *
 * Three prefixes move together and each is a real consequence:
 *
 * - `['document', nodeId]` — the gate, the publish preview, and the document
 *   itself, whose status moves `draft → in_review` on open and back on a
 *   rejection or an expiry (§7.4).
 * - `['approvals']` — this request, and every reviewer's "waiting on me" list.
 * - `['search']` — §9.3 groups `in_review` with `draft`, so opening a request on
 *   a draft changes nothing about who finds it, while the status badge on every
 *   hit is now wrong until this refetches.
 */
function useApprovalMutation<TInput>(
  mutationFn: (input: TInput) => Promise<ApprovalMutationResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['document', data.gate.nodeId] });
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
      void queryClient.invalidateQueries({ queryKey: keys.unreadCount });
    },
  });
}

export interface OpenApprovalInput {
  documentId: string;
  /**
   * Everyone besides the owner, who is added as `required` by trigger whatever
   * this says (§7.3). `requirement` is optional here and defaults to advisory on
   * the server: a required reviewer is a gate, and a gate should be asked for
   * rather than fallen into.
   */
  reviewers?: Array<{ personId: string; requirement?: ReviewerRequirement }>;
}

/** Opening a request is what enters `in_review` (§7.4) — never a status write. */
export function useOpenApprovalRequest() {
  return useApprovalMutation((input: OpenApprovalInput) =>
    api.post<ApprovalMutationResponse>('/api/approvals', input),
  );
}

/**
 * A reviewer's decision — §7.6's immutable record of who decided what and when.
 *
 * Two verbs on the wire rather than one route taking a decision in its body,
 * mirrored here as a `decision` field the caller must spell out. Approving and
 * rejecting are different acts: one clears the gate, the other hands the draft
 * back to its author.
 *
 * `note` is short and has no column — it travels in the event metadata, because
 * §8.1 draws the line and means it: the approval trail records who approved, the
 * comment trail records what was raised.
 */
export function useApprovalDecision(requestId: string) {
  return useApprovalMutation((input: { decision: ReviewerDecision; note?: string }) =>
    api.post<ApprovalMutationResponse>(
      `/api/approvals/${requestId}/${input.decision === 'approved' ? 'approve' : 'reject'}`,
      input.note ? { note: input.note } : {},
    ),
  );
}

/** Adding is `document.edit`; it only ever tightens the gate. */
export function useAddReviewer(requestId: string) {
  return useApprovalMutation((input: { personId: string; requirement: ReviewerRequirement }) =>
    api.post<ApprovalMutationResponse>(`/api/approvals/${requestId}/reviewers`, input),
  );
}

/**
 * Removing is `document.approve`, because it loosens the gate — and the API
 * refuses outright for the owner or for anyone who has already decided, since
 * deleting the row is the one way to erase a decision §7.6 makes immutable.
 */
export function useRemoveReviewer(requestId: string) {
  return useApprovalMutation((personId: string) =>
    api.delete<ApprovalMutationResponse>(`/api/approvals/${requestId}/reviewers/${personId}`),
  );
}

/**
 * Withdrawing a question nobody is pursuing (§7.6's `expired`).
 *
 * The escape hatch from one-open-request-per-document: a required reviewer who
 * has left would otherwise mean no new request can ever be opened here.
 */
export function useExpireApprovalRequest(requestId: string) {
  return useApprovalMutation((input: { reason?: string }) =>
    api.post<ApprovalMutationResponse>(
      `/api/approvals/${requestId}/expire`,
      input.reason ? { reason: input.reason } : {},
    ),
  );
}

// --- acknowledgments (§7.7) ------------------------------------------------

/** What this person still owes. The query behind the nag and the badge. */
export function useOwedAcknowledgments() {
  return useQuery({
    queryKey: keys.acknowledgmentsOwed,
    queryFn: ({ signal }) =>
      api.get<OwedAcknowledgmentsResponse>('/api/acknowledgments/mine', undefined, signal),
    refetchOnWindowFocus: true,
  });
}

/**
 * This person's own record — what they owe and what they have signed.
 *
 * One query rather than two because the halves only read correctly together:
 * "you signed version 1" means something different with version 3 outstanding.
 */
export function useAcknowledgmentHistory() {
  return useQuery({
    queryKey: keys.acknowledgmentHistory,
    queryFn: ({ signal }) =>
      api.get<AcknowledgmentHistoryResponse>('/api/acknowledgments/history', undefined, signal),
    refetchOnWindowFocus: true,
  });
}

export interface ComplianceFilters {
  outstandingOnly?: boolean;
  /** Cycles whose version is no longer published — the historical record. */
  includeSuperseded?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * §7.7's cross-document compliance view, which is `audit.view` and therefore
 * admins.
 *
 * The flags are sent as the literal strings the route accepts, and `false` is
 * dropped rather than sent: `?outstandingOnly=false` would be read as the filter
 * being *on* by any schema doing the obvious coercion, and the API spells out
 * that it refuses to be that schema. Omitting is the unambiguous way to say no.
 */
export function useAcknowledgmentCompliance(filters: ComplianceFilters = {}) {
  return useQuery({
    queryKey: keys.compliance(filters),
    queryFn: ({ signal }) =>
      api.get<ComplianceResponse>(
        '/api/acknowledgments/compliance',
        {
          outstandingOnly: filters.outstandingOnly ? 'true' : undefined,
          includeSuperseded: filters.includeSuperseded ? 'true' : undefined,
          limit: filters.limit,
          offset: filters.offset,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useAcknowledgmentCycle(requestId: string) {
  return useQuery({
    queryKey: keys.acknowledgmentCycle(requestId),
    queryFn: ({ signal }) =>
      api.get<AcknowledgmentCycleResponse>(`/api/acknowledgments/${requestId}`, undefined, signal),
    enabled: requestId !== '',
    // Every read re-resolves the audience, so this is never a cached count of a
    // membership that has moved. Holding it stale would defeat that.
    staleTime: 0,
  });
}

export function useDocumentAcknowledgments(nodeId: string, currentOnly = false) {
  return useQuery({
    queryKey: keys.documentAcknowledgments(nodeId, currentOnly),
    queryFn: ({ signal }) =>
      api.get<DocumentAcknowledgmentsResponse>(
        `/api/acknowledgments/documents/${nodeId}`,
        { currentOnly: currentOnly ? 'true' : undefined },
        signal,
      ),
    enabled: nodeId !== '',
    staleTime: 0,
  });
}

export interface OpenCycleInput {
  audience:
    | { kind: 'role'; role: UserRole }
    | { kind: 'space_grant'; spaceId: string; capability: string };
  /** §7.7 — a calendar day, or none. */
  dueDate?: string | null;
}

/**
 * Opens a cycle against whatever version readers currently resolve to.
 *
 * The body names no version and cannot: §7.7 scopes acknowledgment to the
 * published version so that "everyone acknowledged the expense policy" stops
 * being true after a rewrite.
 */
export function useOpenAcknowledgmentCycle(nodeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OpenCycleInput) =>
      api.post<OpenCycleResponse>(`/api/acknowledgments/documents/${nodeId}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['document', nodeId] });
      void queryClient.invalidateQueries({ queryKey: ['acknowledgments'] });
      void queryClient.invalidateQueries({ queryKey: keys.unreadCount });
    },
  });
}

/**
 * "I have read this."
 *
 * Idempotent on the server — a second press reports the original instant rather
 * than a 409 — so nothing here has to guard against a double submit.
 */
export function useAcknowledge(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AcknowledgeResponse>(`/api/acknowledgments/${requestId}`),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ['document', data.acknowledgment.documentId],
      });
      void queryClient.invalidateQueries({ queryKey: ['acknowledgments'] });
    },
  });
}

/** Used by the router guard, which needs the session before a component renders. */
export function ensureSession(queryClient: QueryClient): Promise<MeResponse> {
  return queryClient.ensureQueryData(meQuery);
}
